/**
 * The period selector and the week axis, shared by every weekly table in the application —
 * Order Activation's activations per week and the Cocon Requests history.
 *
 * Decided with Hugues (26 Oct 2026, docs/stats-proposal-2026-10-26.md): last 4 / 13 / 26 / 52
 * weeks, since the start, or any from–to week; the page opens on 13. Weeks run Monday to
 * Sunday. A week the data does not cover is still on the axis, shown EMPTY — never dropped,
 * never drawn as zero.
 */

import { dateOnly, isoWeekKey } from "@cocon-stats/shared";

export type Preset = "4" | "13" | "26" | "52" | "all";
export const PRESETS: Array<{ key: Preset; label: string }> = [
  { key: "4", label: "Last 4 weeks" },
  { key: "13", label: "Last 13 weeks" },
  { key: "26", label: "Last 26 weeks" },
  { key: "52", label: "Last 52 weeks" },
  { key: "all", label: "Since the start" },
];

export interface Period {
  preset: Preset | null;
  custom: { from: string; to: string } | null;
}
export const DEFAULT_PERIOD: Period = { preset: "13", custom: null };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY = 86_400_000;

export function addDays(iso: string, n: number): string {
  return new Date(dateOnly(iso).getTime() + n * DAY).toISOString().slice(0, 10);
}
/** `26 Oct` */
export function dm(iso: string): string {
  const d = dateOnly(iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}
/** `W43 ’26` */
export function weekLabel(key: string): string {
  return `W${key.slice(6)} ’${key.slice(2, 4)}`;
}

/** One week on the axis: either one the data covers, or one it does not (shown empty). */
export interface AxisWeek<T> {
  key: string;
  start: string;
  end: string;
  data: T | null;
}

/**
 * The axis: the 52 weeks up to the newest the data holds, reaching further back if the data
 * does. `weeks` must carry an ISO week key and its Monday.
 */
export function weekAxis<T extends { week: string; start: string }>(weeks: readonly T[]): AxisWeek<T>[] {
  if (weeks.length === 0) return [];
  const byKey = new Map(weeks.map((w) => [w.week, w]));
  const latest = weeks[weeks.length - 1]!;
  const earliest = weeks[0]!;
  const out: AxisWeek<T>[] = [];
  for (let start = latest.start, i = 0; ; start = addDays(start, -7), i++) {
    if (i >= 52 && start < earliest.start) break;
    const key = isoWeekKey(dateOnly(start));
    out.unshift({ key, start, end: addDays(start, 6), data: byKey.get(key) ?? null });
  }
  return out;
}

/** The weeks of the axis the period selects. */
export function periodWeeks<T>(axis: readonly AxisWeek<T>[], period: Period): AxisWeek<T>[] {
  if (axis.length === 0) return [];
  if (period.custom) {
    const { from, to } = period.custom;
    return axis.filter((w) => w.key >= from && w.key <= to);
  }
  if (period.preset === "all") return axis.slice(Math.max(0, axis.findIndex((w) => w.data)));
  return axis.slice(-Number(period.preset ?? "13"));
}

/** Presets, then From … to …, over the weeks of an axis. */
export function PeriodPicker<T>({
  axis,
  shown,
  period,
  onChange,
}: {
  axis: readonly AxisWeek<T>[];
  shown: readonly AxisWeek<T>[];
  period: Period;
  onChange: (next: Period) => void;
}) {
  const options = axis.map((w) => (
    <option key={w.key} value={w.key}>
      {weekLabel(w.key)} ({dm(w.start)} – {dm(w.end)})
    </option>
  ));
  return (
    <>
      <span className="eyebrow mr-1">Period</span>
      {PRESETS.map((p) => (
        <button
          key={p.key}
          type="button"
          className={`chip${!period.custom && period.preset === p.key ? " chip-active" : ""}`}
          onClick={() => onChange({ preset: p.key, custom: null })}
        >
          {p.label}
        </button>
      ))}
      <span className="ml-2 flex items-center gap-2 text-xs text-slate-500">
        From
        <select
          className="field field-sm"
          style={{ width: "auto" }}
          value={shown[0]?.key ?? ""}
          onChange={(e) => {
            const from = e.target.value;
            const to = shown[shown.length - 1]?.key ?? from;
            onChange({ preset: null, custom: { from, to: to < from ? from : to } });
          }}
        >
          {options}
        </select>
        to
        <select
          className="field field-sm"
          style={{ width: "auto" }}
          value={shown[shown.length - 1]?.key ?? ""}
          onChange={(e) => {
            const to = e.target.value;
            const from = shown[0]?.key ?? to;
            onChange({ preset: null, custom: { from: from > to ? to : from, to } });
          }}
        >
          {options}
        </select>
      </span>
    </>
  );
}
